import React, { useState, useEffect } from 'react';

// ── Types (mirror engine/core/cost-tracker.ts getCostReport) ────────────────
interface CostEntry {
  timestamp: string;
  stage: string;
  slug: string;
  provider: string;
  model: string;
  promptTokens: number;
  completionTokens: number;
  costCents: number;
}
interface CostReport {
  total: number; // cents
  byStage: Record<string, number>;
  byModel: Record<string, number>;
  entries: CostEntry[];
}

interface CostViewProps {
  brand: string;
  onSelectPiece: (slug: string) => void;
  onSelectVolume: (slug: string) => void;
}

// Approximate INR conversion for the ₹-thinking reader. Display-only, clearly
// marked "≈"; the ledger's source of truth is USD cents.
const INR_PER_USD = 83;
const usd = (cents: number) => `$${(cents / 100).toFixed(2)}`;
const inr = (cents: number) => `≈₹${Math.round((cents / 100) * INR_PER_USD).toLocaleString('en-IN')}`;
const pct = (part: number, whole: number) => (whole > 0 ? Math.round((part / whole) * 100) : 0);

function stageColor(stage: string): string {
  switch (stage) {
    case 'pipeline': return 'var(--zop-blue)';
    case 'social': return 'var(--zop-orange)';
    case 'ebook': return 'var(--text-secondary)';
    case 'visual-tldr': return 'var(--zop-green)';
    case 'medium': return 'var(--accent)';
    case 'newsletter': return 'var(--zop-green-dark)';
    case 'geo-probe': return 'var(--danger)';
    case 'outreach': return 'var(--warning)';
    default: return 'var(--text-tertiary)';
  }
}

// ── Shared styles (one card style, one bar row — keeps the page visually calm) ─
const CARD: React.CSSProperties = { background: 'var(--bg-surface)', border: '1px solid var(--border)', padding: '18px 20px', marginBottom: 16 };
const EYEBROW: React.CSSProperties = { fontSize: 'var(--fs-eyebrow)', textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-tertiary)', fontWeight: 700, margin: '0 0 14px' };
const NUM: React.CSSProperties = { fontVariantNumeric: 'tabular-nums' };
const MUTED: React.CSSProperties = { color: 'var(--text-tertiary)', fontWeight: 400 };

// A labelled horizontal bar: [label] [====bar====] [value (pct)]. Reused by the
// daily + by-stage sections so both read as one component.
function BarRow({ label, color, value, frac, total, labelWidth = 96 }: {
  label: React.ReactNode; color: string; value: number; frac: number; total: number; labelWidth?: number;
}) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 7 }}>
      <div style={{ width: labelWidth, fontSize: 'var(--fs-small)', color: 'var(--text-secondary)', textAlign: 'right', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{label}</div>
      <div style={{ flex: 1, background: 'var(--border-subtle, var(--border))', height: 16, minWidth: 40 }}>
        <div style={{ width: `${Math.max(2, frac * 100)}%`, height: '100%', background: color }} />
      </div>
      <div style={{ width: 120, fontSize: 'var(--fs-small)', color: 'var(--text-primary)', fontWeight: 600, ...NUM }}>
        {usd(value)} <span style={MUTED}>({pct(value, total)}%)</span>
      </div>
    </div>
  );
}

interface PieceRow { slug: string; total: number; calls: number; stages: string[] }
function byPiece(entries: CostEntry[]): PieceRow[] {
  const map = new Map<string, PieceRow>();
  for (const e of entries) {
    const key = e.slug || '(unattributed)';
    const row = map.get(key) ?? { slug: key, total: 0, calls: 0, stages: [] };
    row.total += e.costCents; row.calls += 1;
    if (!row.stages.includes(e.stage)) row.stages.push(e.stage);
    map.set(key, row);
  }
  return [...map.values()].sort((a, b) => b.total - a.total);
}

interface DayRow { date: string; total: number; calls: number }
// One row per calendar day (UTC date of the timestamp), most recent first — so
// "every day's spend is there, date-wise". Days with no spend are omitted.
function byDay(entries: CostEntry[]): DayRow[] {
  const map = new Map<string, DayRow>();
  for (const e of entries) {
    const date = (e.timestamp || '').slice(0, 10) || 'unknown';
    const row = map.get(date) ?? { date, total: 0, calls: 0 };
    row.total += e.costCents; row.calls += 1;
    map.set(date, row);
  }
  return [...map.values()].sort((a, b) => b.date.localeCompare(a.date));
}

function StageChip({ stage }: { stage: string }) {
  return <span style={{ display: 'inline-block', fontSize: 10, fontWeight: 600, padding: '1px 7px', marginRight: 4, borderRadius: 2, color: '#fff', background: stageColor(stage), whiteSpace: 'nowrap' }}>{stage}</span>;
}

const TH: React.CSSProperties = { textAlign: 'left', fontSize: 'var(--fs-eyebrow)', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-tertiary)', fontWeight: 700, padding: '0 12px 8px' };
const TD: React.CSSProperties = { padding: '9px 12px', fontSize: 'var(--fs-small)', color: 'var(--text-secondary)', borderTop: '1px solid var(--border)' };

export default function CostView({ brand, onSelectPiece, onSelectVolume }: CostViewProps) {
  const [report, setReport] = useState<CostReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setReport(null); setLoading(true); setError(null);
    fetch(`/api/${brand}/cost`)
      .then((r) => { if (!r.ok) return r.json().then((d: { error?: string }) => { throw new Error(d.error || `HTTP ${r.status}`); }); return r.json(); })
      .then((d: CostReport) => { setReport(d); setLoading(false); })
      .catch((err: Error) => { setError(err.message); setLoading(false); });
  }, [brand]);

  if (loading) return <div style={{ padding: '24px 0', color: 'var(--text-tertiary)', fontSize: 'var(--fs-small)' }}>Loading spend…</div>;
  if (error) return <div style={{ padding: '24px 0', color: 'var(--danger)', fontSize: 'var(--fs-small)' }}>Error: {error}</div>;
  if (!report) return null;

  const { total, byStage, byModel, entries } = report;

  if (entries.length === 0) {
    return (
      <div style={{ padding: '24px 0', maxWidth: 720 }}>
        <div style={CARD}>
          <p style={EYEBROW}>No spend recorded yet</p>
          <div style={{ fontSize: 'var(--fs-small)', color: 'var(--text-secondary)', lineHeight: 1.6 }}>
            The cost ledger fills as the engine makes LLM calls — a blog, a carousel, an ebook chapter, a
            newsletter, or a GEO probe. Publishing and the topic suggester are deterministic and cost nothing.
          </div>
        </div>
      </div>
    );
  }

  const stages = Object.entries(byStage).sort((a, b) => b[1] - a[1]);
  const models = Object.entries(byModel).sort((a, b) => b[1] - a[1]);
  const pieces = byPiece(entries);
  const days = byDay(entries);
  const maxStage = stages[0]?.[1] ?? 1;
  const maxDay = Math.max(...days.map((d) => d.total), 1);

  return (
    <div style={{ padding: '20px 0', maxWidth: 860 }}>
      {/* Total */}
      <div style={CARD}>
        <p style={EYEBROW}>Total LLM spend</p>
        <div style={{ fontSize: 34, fontWeight: 800, color: 'var(--text-primary)', lineHeight: 1, ...NUM }}>{usd(total)}</div>
        <div style={{ fontSize: 'var(--fs-small)', color: 'var(--text-secondary)', marginTop: 6 }}>
          {inr(total)} · {entries.length} calls · {days[days.length - 1]?.date} → {days[0]?.date}
        </div>
      </div>

      {/* Daily — date-wise */}
      <div style={CARD}>
        <p style={EYEBROW}>Spend per day</p>
        {days.map((d) => (
          <BarRow key={d.date} label={d.date} color="var(--accent)" value={d.total} frac={d.total / maxDay} total={total} labelWidth={92} />
        ))}
      </div>

      {/* By stage */}
      <div style={CARD}>
        <p style={EYEBROW}>Where it goes — by stage</p>
        {stages.map(([stage, cents]) => (
          <BarRow key={stage} label={stage} color={stageColor(stage)} value={cents} frac={cents / maxStage} total={total} />
        ))}
      </div>

      {/* Per piece / ebook */}
      <div style={CARD}>
        <p style={EYEBROW}>Spend per blog / ebook</p>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr>
              <th style={TH}>Piece</th><th style={TH}>Stages</th>
              <th style={{ ...TH, textAlign: 'right' }}>Calls</th><th style={{ ...TH, textAlign: 'right' }}>Cost</th>
            </tr></thead>
            <tbody>
              {pieces.map((p) => {
                const isEbook = p.stages.includes('ebook');
                return (
                  <tr key={p.slug}>
                    <td style={TD}>
                      {p.slug === '(unattributed)'
                        ? <span style={{ color: 'var(--text-tertiary)', fontStyle: 'italic' }}>(unattributed)</span>
                        : <button onClick={() => (isEbook ? onSelectVolume(p.slug) : onSelectPiece(p.slug))} style={{ background: 'none', border: 'none', padding: 0, color: 'var(--accent)', cursor: 'pointer', fontSize: 'var(--fs-small)', textAlign: 'left', fontWeight: 600 }}>{isEbook ? '📚 ' : ''}{p.slug}</button>}
                    </td>
                    <td style={TD}>{p.stages.map((s) => <StageChip key={s} stage={s} />)}</td>
                    <td style={{ ...TD, textAlign: 'right', ...NUM }}>{p.calls}</td>
                    <td style={{ ...TD, textAlign: 'right', color: 'var(--text-primary)', fontWeight: 600, ...NUM }}>{usd(p.total)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* By model */}
      <div style={CARD}>
        <p style={EYEBROW}>By model</p>
        {models.map(([model, cents]) => (
          <div key={model} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 'var(--fs-small)', padding: '6px 0', borderTop: '1px solid var(--border)' }}>
            <span style={{ color: 'var(--text-secondary)' }}>{model}</span>
            <span style={{ color: 'var(--text-primary)', fontWeight: 600, ...NUM }}>{usd(cents)} <span style={MUTED}>({pct(cents, total)}%)</span></span>
          </div>
        ))}
        <div style={{ marginTop: 12, fontSize: 11, color: 'var(--text-tertiary)', lineHeight: 1.5 }}>
          Every LLM call is logged to the ledger. ₹ is approximate (≈{INR_PER_USD}/$); the ledger records USD. Publishing and the topic suggester are deterministic — they never appear here.
        </div>
      </div>
    </div>
  );
}
